/**
 * Pure helpers for rich-text automation emails + attachments.
 * No React / DOM / API imports so they can be unit-tested with plain node.
 */

const MB = 1024 * 1024;

export const ATTACHMENT_LIMITS = {
  maxFiles: 5,
  /** Hard upload cap (backend rejects larger files). */
  maxUploadBytes: 50 * MB,
  /**
   * Per-file cap for real MIME attachments (images, PDF, audio, video).
   * Kept under typical SES raw-message limits (~40 MB including body).
   */
  maxAttachBytes: 30 * MB,
  /** Combined size of real attachments per email; overflow falls back to links. */
  maxTotalAttachBytes: 30 * MB,
};

export const ALLOWED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "audio/mpeg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/ogg",
  "audio/webm",
  "video/mp4",
  "video/quicktime",
  "video/webm",
];

export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_TYPES.join(",");

export const getAttachmentKind = (mimeType = "") => {
  const type = String(mimeType || "").toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  if (type.startsWith("audio/")) return "audio";
  if (type.startsWith("video/")) return "video";
  return "file";
};

export const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < MB) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / MB).toFixed(1)} MB`;
};

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Legacy plain-text body -> HTML (blank line = paragraph, newline = <br>). */
export const plainTextToHtml = (text) => {
  const normalized = String(text || "").replace(/\r\n?/g, "\n").trim();
  if (!normalized) return "";
  return normalized
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

/** HTML body -> plain text (used when an email workflow becomes an in-app notification). */
export const htmlToPlainText = (html) =>
  String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Email workflows always edit/save HTML. Converts a legacy plain-text body once.
 * Returns the same object when nothing needs to change.
 */
export const ensureHtmlEmailBody = (workflow) => {
  if (!workflow || workflow.action !== "email") return workflow;
  if (workflow.bodyFormat === "html") return workflow;
  return {
    ...workflow,
    bodyFormat: "html",
    message: plainTextToHtml(workflow.message),
  };
};

/**
 * Apply body-format side effects of an action change:
 * -> email: legacy text becomes HTML; -> notify: HTML becomes plain text.
 */
export const applyActionBodyFormat = (workflow) => {
  if (!workflow) return workflow;
  if (workflow.action === "email") return ensureHtmlEmailBody(workflow);
  if (workflow.action === "notify" && workflow.bodyFormat === "html") {
    const { bodyFormat, ...rest } = workflow;
    return { ...rest, message: htmlToPlainText(workflow.message) };
  }
  return workflow;
};

/**
 * Delivery mode the email will most likely use for each attachment, in order.
 * Mirrors the backend: files (including video) ride as MIME attachments until
 * the 30 MB per-file / total budget is used; only overflow becomes links.
 * Stored `deliverAs: "link"` from older uploads is ignored when size allows attach.
 */
export const planAttachmentDelivery = (attachments = []) => {
  let attachedBytes = 0;
  const items = (attachments || []).map((item) => {
    const size = Number(item?.size) || 0;
    const canAttach =
      size > 0 &&
      size <= ATTACHMENT_LIMITS.maxAttachBytes &&
      attachedBytes + size <= ATTACHMENT_LIMITS.maxTotalAttachBytes;
    if (canAttach) attachedBytes += size;
    return { ...item, effectiveDeliverAs: canAttach ? "attachment" : "link" };
  });
  return { items, attachedBytes };
};

/** Client-side check before upload. Returns an error message or "". */
export const validateAttachmentFile = (file, existing = []) => {
  if (!file) return "No file selected";
  if ((existing || []).length >= ATTACHMENT_LIMITS.maxFiles) {
    return `You can attach up to ${ATTACHMENT_LIMITS.maxFiles} files`;
  }
  if (!ALLOWED_ATTACHMENT_TYPES.includes(String(file.type || "").toLowerCase())) {
    return `"${file.name}" is not a supported file type (images, PDF, audio or video only)`;
  }
  if ((Number(file.size) || 0) > ATTACHMENT_LIMITS.maxUploadBytes) {
    return `"${file.name}" is too large. Files must be 50 MB or smaller`;
  }
  return "";
};
